import BigNumber from 'bignumber.js';
import { ActionCableService, distinctBigNumberUntilChanged, sharePublishReplay, swalloError } from 'ngx-libs';
import { BehaviorSubject, combineLatest, Observable, of, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { ContractService } from '../services/contract.service';
import { EthWalletService } from '../services/eth-wallet.service';
import { MainService } from '../services/main.service';
import { Base } from './base';
import { Market } from './market';
import { MAX_INT } from './vault/vault';
import { PaymasterMethod } from './paymaster-method';
import { ShareToken } from './share-token';

const MIN_MAX_CAPACITY = new BigNumber(0);

const greeks = require('greeks');
export class TradingVault extends Base {
  id: number;
  name: string;
  description: string;
  apy: number;
  avgApy: number;
  type: string;
  createdAt: number;
  profit30D: number;
  projectedApy: number;

  token: Observable<ShareToken>;
  quoteApproved: Observable<boolean>;
  quoteAllowance: Observable<BigNumber>;

  marginBalance: Observable<BigNumber>;
  equity: Observable<BigNumber>;
  equityDisplay: Observable<string>;
  upnl: Observable<BigNumber>;
  available: Observable<BigNumber>;
  realAvailable: Observable<BigNumber>;
  healthFactor: Observable<number>;
  totalShares: Observable<BigNumber>;
  priceOfShare: Observable<BigNumber>;
  utilization: Observable<number>;
  realUtilization: Observable<number>;
  myShares: Observable<BigNumber>;
  myValue: Observable<BigNumber>;
  positions: Observable<any[]>;
  staked: Observable<boolean>;
  netDelta: Observable<BigNumber>;
  myNetDelta: Observable<BigNumber>;
  snapshots: Observable<any[]>;
  allApy: Observable<number>;
  last30dApy: Observable<number>;
  costPerShare: Observable<BigNumber>;

  userSubscription: Observable<BigNumber>;
  userRedemption: Observable<BigNumber>;
  subscriptionFunds: Observable<BigNumber>;
  redemptionShares: Observable<BigNumber>;
  maxCapacity: Observable<BigNumber>;
  capacity: Observable<BigNumber>;
  percent: Observable<number>;
  realizedPriceOfShare: Observable<BigNumber>;

  retired: Observable<boolean>;

  expanded = false;
  toggling = false;

  private dataSubject = new BehaviorSubject<any>({});

  constructor(
    public contract: string,
    protected abi: any,
    protected accountService: AccountService,
    protected contractService: ContractService,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private mainService: MainService,
    public market: Market
  ) {
    super(contract, abi, accountService, contractService);

    this.retired = timer(0, 60000).pipe(
      mergeMap(() => this.getRetired().pipe(swalloError())),
      sharePublishReplay(1)
    );

    const lastFunds = timer(0, 6000).pipe(
      mergeMap(() => this.getLastFunds().pipe(swalloError())),
      sharePublishReplay(1)
    );

    this.maxCapacity = this.dataSubject.pipe(map((data) => {
      return data.maxCapacity.lt(MIN_MAX_CAPACITY) ? MIN_MAX_CAPACITY : data.maxCapacity;
    }));

    const adjCapacity = this.dataSubject.pipe(map((data) => {
      return data.maxCapacity.lt(MIN_MAX_CAPACITY) ? MIN_MAX_CAPACITY.minus(data.maxCapacity) : new BigNumber(0);
    }));

    const vaultBalance = this.market.vault.chain.getBalanceOfCache(this.contract);
    const balance = this.retired.pipe(
      mergeMap((r) => r ? lastFunds : vaultBalance)
    );
    const vaultSource = combineLatest([balance, timer(0, 60000)]).pipe(sharePublishReplay(1));
    const token = this.dataSubject.pipe(
      map((data) => data.token),
      distinctUntilChanged()
    );

    this.token = token.pipe(
      filter((t) => t),
      take(1),
      map(
        (t) =>
          new ShareToken(
            t,
            this.contract,
            18,
            this.accountService,
            this.ethWalletService,
            this.contractService,
            this.actionCableService,
            this.mainService
          )
      ),
      sharePublishReplay(1)
    );

    this.totalShares = combineLatest([this.dataSubject, this.market.pools]).pipe(
      debounceTime(10),
      map(([data]) => data.totalShares)
    );

    this.positions = vaultSource.pipe(
      switchMap(() => this.market.vault.getPositions(this.contract).pipe(swalloError())),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      sharePublishReplay(1)
    );

    const vaultAccountInfo = vaultSource.pipe(
      switchMap(() => this.market.vault.getAccountInfo(this.contract)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      sharePublishReplay(1)
    );

    const retiredAccountInfo = lastFunds.pipe(
      map((f) => {
        return {
          marginBalance: f,
          equity: f,
          available: f,
          upnl: new BigNumber(0),
          healthFactor: new BigNumber(Infinity)
        }
      })
    );

    const accountInfo = this.retired.pipe(
      mergeMap((r) => r ? retiredAccountInfo : vaultAccountInfo)
    );

    this.marginBalance = accountInfo.pipe(
      map((a) => a.marginBalance || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.equity = accountInfo.pipe(
      map((a) => a.equity || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.equityDisplay = this.equity.pipe(
      map((e) => {
        let value = e.toNumber();
        let u = '';
        if (value > 1000000) {
          value /= 1000000;
          u = 'm';
        } else if (value > 1000) {
          value /= 1000;
          u = 'k';
        }
        return `$${value.toFixed(1)}${u}`;
      })
    );

    this.upnl = accountInfo.pipe(
      map((a) => a.upnl || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.available = accountInfo.pipe(
      map((a) => a.available || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.realAvailable = this.available;
    this.healthFactor = accountInfo.pipe(
      map((a) => {
        if (a.healthFactor) {
          return a.healthFactor.toDecimal().eq(MAX_INT) ? null : a.healthFactor.toNumber();
        } else {
          return null;
        }
      }),
      distinctUntilChanged()
    );

    this.utilization = combineLatest([this.equity, this.available]).pipe(
      debounceTime(0),
      map(([e, a]) => (e.lte(0) ? null : 1 - a.div(e).toNumber())),
      distinctUntilChanged()
    );

    this.realUtilization = this.utilization;

    this.priceOfShare = combineLatest([this.equity, this.totalShares]).pipe(
      map(([v, t]) => (t.eq(0) ? new BigNumber(1) : v.div(t).truncate(18)))
    );

    this.realizedPriceOfShare = combineLatest([balance, this.totalShares]).pipe(
      map(([v, t]) => (t.eq(0) ? new BigNumber(1) : v.div(t).truncate(18)))
    );

    let approved = false;
    const account = this.accountService.account.pipe(
      distinctUntilChanged(),
      tap(() => {
        approved = false;
      })
    );

    this.quoteAllowance = account.pipe(
      switchMap((a) =>
        timer(0, 5000).pipe(
          filter(() => !approved),
          switchMap(() =>
            a ? this.market.quote.allowance(a, this.contract).pipe(swalloError()) : of(new BigNumber(0))
          )
        )
      ),
      distinctBigNumberUntilChanged()
    );

    this.quoteApproved = this.quoteAllowance.pipe(
      map((v) => v.gt(0)),
      tap((r) => {
        approved = r;
      }),
      sharePublishReplay(1)
    );

    this.myShares = this.token.pipe(switchMap((t) => t.balance));

    this.myValue = combineLatest([this.priceOfShare, this.myShares]).pipe(
      map(([price, shares]) => price.times(shares))
    );

    this.staked = this.myShares.pipe(map((s) => s.gt(0)));

    const spotPrice = this.market.spotPricer.price;
    this.netDelta = combineLatest([this.positions, spotPrice, this.equity]).pipe(
      map(([positions, sp]) => {
        const spot = sp.toNumber();
        return new BigNumber(
          positions.reduce((netDelta, position) => {
            const iv = this.market.contractVault.optionMarket
              .getIv(position.expiry, position.strike.toDecimal(), position.isCall, position.size.gt(0))
              .fromDecimal()
              .toNumber();
            const time = (position.expiry - Date.now() / 1000) / 31536000;
            const delta = greeks.getDelta(
              spot,
              position.strike.toNumber(),
              time,
              iv,
              0.06,
              position.isCall ? 'call' : 'put'
            );
            return delta * position.size.toNumber() + netDelta;
          }, 0)
        );
      }),
      sharePublishReplay(1)
    );

    this.myNetDelta = combineLatest([this.myShares, this.totalShares, this.netDelta]).pipe(
      map(([s, t, n]) => s.div(t).times(n))
    );

    this.snapshots = timer(0, 60000).pipe(
      switchMap(() => this.api.poolSnapshots.query().pipe(swalloError())),
      map((json) => json.poolSnapshots),
      sharePublishReplay(1)
    );

    this.allApy = this.priceOfShare.pipe(
      map((p) => {
        const timeRatio = (365 * 86400000) / (Date.now() - this.createdAt * 1000);
        return p.eq(0) ? 0 : p.toNumber() ** timeRatio - 1;
      })
    );

    this.last30dApy = timer(0, 60000).pipe(
      mergeMap(() => {
        if (Date.now() / 1000 - this.createdAt < 86400 * 30) {
          return this.allApy;
        } else {
          return of(this.avgApy > 0 ? this.avgApy : this.avgApy / 12);
        }
      })
    );

    this.costPerShare = combineLatest([this.accountService.account, balance]).pipe(
      debounceTime(300),
      switchMap(([a]) =>
        a ? this.api.accounts.of(a).claims.getTotal() : of({})
      ),
      map((result) => result.price),
      sharePublishReplay(1)
    );

    const received = this.accountService.account.pipe(
      switchMap((a) => {
        if (a) {
          return this.actionCableService
            .received({
              channel: 'TradingVaults::UserRoundChannel',
              trading_vault_id: this.id,
              account: a.toLocaleLowerCase()
            })
            .pipe(startWith(0), debounceTime(10));
        } else {
          return of(0);
        }
      })
    );

    const userRound = combineLatest([this.accountService.account, received, timer(0, 60000)]).pipe(
      mergeMap(([a]) => a ? this.api.userRounds.getCurrent(a) : of({ userRound: { redemption: new BigNumber(0), subscription: new BigNumber(0)}})),
      map((json) => json.userRound),
      sharePublishReplay(1)
    );

    this.userSubscription = userRound.pipe(
      map((r) => r.subscription)
    );

    this.userRedemption = userRound.pipe(
      map((r) => r.redemption)
    );

    const roundReceived = this.accountService.account.pipe(
      switchMap((a) => {
        if (a) {
          return this.actionCableService
            .received({
              channel: 'TradingVaults::RoundChannel',
              trading_vault_id: this.id
            })
            .pipe(startWith(0), debounceTime(10));
        } else {
          return of(0);
        }
      })
    );

    const round = combineLatest([roundReceived, timer(0, 60000)]).pipe(
      mergeMap(() => this.api.rounds.getCurrent()),
      map((json) => json.round),
      sharePublishReplay(1)
    );

    this.subscriptionFunds = round.pipe(
      map((r) => r.subscriptionFunds)
    );

    this.redemptionShares = round.pipe(
      map((r) => r.redemptionShares)
    );

    this.capacity = combineLatest([balance, this.subscriptionFunds, this.redemptionShares, this.totalShares, adjCapacity]).pipe(
      map(([b, s, r, t, a]) => {
        const redemptionFunds = t.gt(0) ? r.times(b).div(t).times(0.999) : new BigNumber(0);
        return b.plus(s).minus(redemptionFunds).plus(a);
      }),
      sharePublishReplay(1)
    );

    this.percent = combineLatest([this.capacity, this.maxCapacity]).pipe(
      map(([c, m]) => c.div(m).toNumber())
    );
  }

  get api() {
    return this.mainService.tradingVaults.of(this.id.toString());
  }

  updateData(data: any) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.apy = data.apy;
    this.avgApy = data.avgApy;
    this.createdAt = data.createdAt;
    this.profit30D = data.avgApy / 12;
    this.projectedApy = data.projectedApy;
    // this.roundId = data.roundId;
    this.market.contractVault.config.setPoolReservedRate(this.contract, data.reservedRate.toDecimal());
    this.market.contractVault.config.setPoolPaused(this.contract, data.paused);
    this.dataSubject.next(data);
  }

  getToken() {
    return this.call((i) => i.methods.token());
  }

  getRetired() {
    return this.call((i) => i.methods.retired());
  }

  getLastFunds() {
    return this.call((i) => i.methods.lastFunds()).pipe(map((v) => new BigNumber(v).fromDecimal()));
  }

  approveQuote(paymasterMethod?: PaymasterMethod) {
    return this.market.quote.approveAll(this.contract, paymasterMethod);
  }

  trySubscribe(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.estimateGas((i) => i.methods.subscribe(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  subscribe(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.subscribe(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  tryUnsubscribe(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.estimateGas((i) => i.methods.unsubscribe(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  unsubscribe(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.unsubscribe(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  tryRedeem(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.estimateGas((i) => i.methods.redeem(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  redeem(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.redeem(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  tryUnredeem(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.estimateGas((i) => i.methods.unredeem(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  unredeem(amount: any, paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.unredeem(new BigNumber(amount).toDecimal().toString(10)), {
      paymasterMethod
    });
  }

  withdraw(paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.withdraw(), {
      paymasterMethod
    });
  }

  catchError() {
    return catchError((e) =>
      this.market.vault.instances.readonlyInstance.pipe(
        mergeMap((instance) => this.tryParseCustomError(e, [instance._errorsInterface]))
      )
    );
  }
}
